import React, { createContext, useContext, useEffect, useState } from "react";
import api, { clearStoredToken, refreshAccessToken } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null=checking, false=unauth, object=authed
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      try {
        await refreshAccessToken();
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        clearStoredToken();
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refresh();
      } catch {
        if (!cancelled) {
          setUser(false);
          setLoading(false);
        }
      }
    })();
    // Hard stop: never leave the shell spinning if the API is wedged.
    const watchdog = setTimeout(() => {
      if (!cancelled) setLoading((prev) => {
        if (prev) setUser((u) => (u === null ? false : u));
        return false;
      });
    }, 25000);
    return () => {
      cancelled = true;
      clearTimeout(watchdog);
    };
  }, []);

  const login = async (email, password, remember = true) => {
    const { data } = await api.post("/auth/login", { email, password, remember });
    clearStoredToken();
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    clearStoredToken();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
